import React, { useEffect, useMemo, useState } from 'react';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo } from '../../utils';
import Card from '../../components/elements/Card';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import ContactPersonNotice from '../../components/common/ContactPersonNotice';

const fallbackMissionImage = require('./../../assets/images/fallback/mission.png');

const PLATFORM_PATTERNS = [
  { name: 'instagram', label: 'Instagram', match: /instagram\.|instagr\.am/i },
  { name: 'tiktok', label: 'TikTok', match: /tiktok\./i, icon: 'tik-tok' },
  { name: 'facebook', label: 'Facebook', match: /facebook\.|fb\.watch|fb\.com/i },
  { name: 'youtube', label: 'YouTube', match: /youtube\.|youtu\.be/i },
  { name: 'linkedin', label: 'LinkedIn', match: /linkedin\./i },
];

const detectPlatform = (url) => {
  if (!url) return null;
  return PLATFORM_PATTERNS.find((p) => p.match.test(url)) || null;
};

const StatusPill = ({ approved, hasSubmission }) => {
  const { t } = useTranslation();
  if (!hasSubmission) {
    return (
      <span className="status-pill status-pill--todo">
        <Icon name="circle" /> {t('missionStatusTodo')}
      </span>
    );
  }
  if (approved) {
    return (
      <span className="status-pill status-pill--approved">
        <Icon name="check-circle" /> {t('missionStatusApproved')}
      </span>
    );
  }
  return (
    <span className="status-pill status-pill--pending">
      <Icon name="duration-alt" /> {t('missionStatusPending')}
    </span>
  );
};

// Picture missions and video submissions share this page: both are "missions"
// for the team, they only differ in what gets handed in (a photo vs. a link).
const Page = () => {
  const { t } = useTranslation();
  const [pictureInfo, setPictureInfo] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [loadingPictures, setLoadingPictures] = useState(true);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/missions?own_team=1', setPictureInfo, setLoadingPictures, setError);
    fetchUserInfo('/social_missions?own_team=1', setVideoInfo, setLoadingVideos, setError);
  }, []);

  const loading = loadingPictures || loadingVideos;

  // One flat list: each entry carries its type plus the team's submission.
  const items = useMemo(() => {
    const pictureSubs = pictureInfo?.submissions || [];
    const videoSubs = videoInfo?.submissions || [];
    const pictures = (pictureInfo?.missions || []).map((mission) => {
      const submission = pictureSubs.find((s) => s.picture_mission_in_event_id === mission.id);
      return { type: 'picture', mission, submission, approved: !!submission?.is_approved };
    });
    const videos = (videoInfo?.missions || []).map((mission) => {
      const submission = videoSubs.find((s) => s.social_mission_in_event_id === mission.id);
      return { type: 'video', mission, submission, approved: !!submission?.approved };
    });
    return [...pictures, ...videos];
  }, [pictureInfo, videoInfo]);

  const stats = useMemo(() => {
    if (items.length === 0) return null;
    let approved = 0;
    let pending = 0;
    let earned = 0;
    items.forEach(({ mission, submission, approved: isApproved }) => {
      if (isApproved) {
        approved += 1;
        earned += mission.points || 0;
      } else if (submission) {
        pending += 1;
      }
    });
    return { total: items.length, approved, pending, earned };
  }, [items]);

  return (
    <Base>
      <Back to="/team" />
      <Title>
        <Icon name="camera" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('missions')}
      </Title>

      <ContactPersonNotice userInfo={pictureInfo} />

      <p className="missions__intro mx-3 mb-3">{t('missionsIntro')}</p>

      {stats && stats.total > 0 && (
        <div className="social-missions__progress mx-3 mb-3">
          <div className="social-missions__progress-stats">
            <span className="social-missions__stat">
              <strong>{stats.approved}</strong>
              <span>/{stats.total} {t('approved')}</span>
            </span>
            <span className={`points-chip ${stats.earned > 0 ? 'points-chip--earned' : ''}`} style={{ marginTop: 0 }}>
              <Icon name="star" />
              {stats.earned > 0 ? '+' : ''}{stats.earned} {t('pts')}
            </span>
          </div>
          <div className="social-missions__progress-bar" aria-hidden="true">
            <div
              className="social-missions__progress-bar-fill social-missions__progress-bar-fill--approved"
              style={{ width: `${(stats.approved / stats.total) * 100}%` }}
            />
            <div
              className="social-missions__progress-bar-fill social-missions__progress-bar-fill--pending"
              style={{ width: `${(stats.pending / stats.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {items.map(({ type, mission, submission, approved }) => {
        const isVideo = type === 'video';
        const link = isVideo ? (submission?.social_media_url || submission?.caption) : null;
        const platform = isVideo ? detectPlatform(link) : null;
        const imageUrl = (isVideo ? mission.photo_url : submission?.image_url || mission.photo_url) || fallbackMissionImage;

        return (
          <Card
            key={`${type}-${mission.id}`}
            onClick={() => sessionStorage.setItem('missionId', mission.id)}
            to={isVideo ? '/social-mission' : '/mission'}
            mx="3"
            mb="2"
            style={{ opacity: mission.is_active ? 1 : 0.55 }}
            className="mission-card mission-card--social"
          >
            <div className="mission-card__status">
              <span className={`mission-type-chip mission-type-chip--${type}`}>
                <Icon name={isVideo ? 'play-alt' : 'camera'} />
                {t(isVideo ? 'video' : 'photo')}
              </span>
              <StatusPill approved={approved} hasSubmission={!!submission} />
            </div>
            <div className="mission-card__body">
              <div
                className="mission-card__media"
                style={{ backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              >
                {platform && (
                  <span className={`platform-badge platform-badge--${platform.name}`} title={platform.label}>
                    <Icon name={platform.icon || platform.name} pack="brands" theme="light" />
                  </span>
                )}
              </div>

              <div className="mission-card__content">
                <div className="mission-card__title">{mission.name}</div>

                {submission ? (
                  <>
                    {isVideo ? (
                      <p className="mission-card__meta">
                        <strong>{t('submittedLink')}:</strong>{' '}
                        {link ? (
                          <a
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ wordBreak: 'break-all' }}
                          >
                            {link}
                          </a>
                        ) : (
                          '-'
                        )}
                      </p>
                    ) : (
                      <p className="mission-card__meta">
                        <strong>{t('uploadedBy')}:</strong> {submission.uploaded_by_name}
                      </p>
                    )}
                    {submission.uploaded_at && (
                      <p className="mission-card__meta">
                        {new Date(submission.uploaded_at).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mission-card__desc">{mission.description}</p>
                )}

                <span className={`points-chip ${approved ? 'points-chip--earned' : ''}`}>
                  <Icon name="star" />
                  {approved ? '+' : ''}{mission.points || 0} {t('pts')}
                </span>
              </div>
            </div>
          </Card>
        );
      })}

      {loading && items.length === 0 && <Loading size="lg" />}

      {!loading && items.length === 0 && (
        <p style={{ textAlign: 'center', marginTop: '2rem' }}>
          {error ? (error || t('somethingWentWrong')) : t('noMissionsAvailable')}
        </p>
      )}
    </Base>
  );
};

export default Page;
